import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getAccountSettings, getUserProfile, listGroups } from '../services/api';
import { useAuth } from './AuthContext';

const SessionDataContext = createContext();

const EMPTY_ACCOUNT_SETTINGS = null;
const EMPTY_PROFILE = null;
const EMPTY_GROUPS = [];

const ACCOUNT_SETTINGS_TTL_MS = 10 * 60 * 1000;
const PROFILE_TTL_MS = 2 * 60 * 1000;
const GROUPS_TTL_MS = 5 * 60 * 1000;

const createDatasetState = (data) => ({
  data,
  lastFetchedAt: 0,
  isFetching: false,
  error: null,
});

export const SessionDataProvider = ({ children }) => {
  const { user, token } = useAuth();

  const [accountSettings, setAccountSettings] = useState(createDatasetState(EMPTY_ACCOUNT_SETTINGS));
  const [profile, setProfile] = useState(createDatasetState(EMPTY_PROFILE));
  const [groups, setGroups] = useState(createDatasetState(EMPTY_GROUPS));

  const accountSettingsInFlightRef = useRef(null);
  const profileInFlightRef = useRef(null);
  const groupsInFlightRef = useRef(null);

  const accountSettingsRef = useRef(accountSettings);
  const profileRef = useRef(profile);
  const groupsRef = useRef(groups);
  const lastUserIdRef = useRef(null);

  useEffect(() => {
    accountSettingsRef.current = accountSettings;
  }, [accountSettings]);

  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);

  const resetSessionData = useCallback(() => {
    setAccountSettings(createDatasetState(EMPTY_ACCOUNT_SETTINGS));
    setProfile(createDatasetState(EMPTY_PROFILE));
    setGroups(createDatasetState(EMPTY_GROUPS));
    accountSettingsInFlightRef.current = null;
    profileInFlightRef.current = null;
    groupsInFlightRef.current = null;
  }, []);

  useEffect(() => {
    const currentUserId = user?.id || null;
    if (lastUserIdRef.current !== currentUserId) {
      lastUserIdRef.current = currentUserId;
      resetSessionData();
    }
  }, [user?.id, resetSessionData]);

  useEffect(() => {
    const onInvalidateAccountSettings = () => {
      setAccountSettings((prev) => ({ ...prev, lastFetchedAt: 0 }));
    };
    const onInvalidateProfile = () => {
      setProfile((prev) => ({ ...prev, lastFetchedAt: 0 }));
    };
    const onInvalidateGroups = () => {
      setGroups((prev) => ({ ...prev, lastFetchedAt: 0 }));
    };

    window.addEventListener('prompthub:invalidate-account-settings', onInvalidateAccountSettings);
    window.addEventListener('prompthub:invalidate-profile', onInvalidateProfile);
    window.addEventListener('prompthub:invalidate-groups', onInvalidateGroups);

    return () => {
      window.removeEventListener('prompthub:invalidate-account-settings', onInvalidateAccountSettings);
      window.removeEventListener('prompthub:invalidate-profile', onInvalidateProfile);
      window.removeEventListener('prompthub:invalidate-groups', onInvalidateGroups);
    };
  }, []);

  const isFresh = useCallback((lastFetchedAt, ttlMs) => {
    if (!lastFetchedAt) return false;
    return Date.now() - lastFetchedAt < ttlMs;
  }, []);

  const getAccountSettingsCached = useCallback(async ({ force = false } = {}) => {
    if (!token || !user?.id) return accountSettingsRef.current.data;

    const current = accountSettingsRef.current;
    if (!force && isFresh(current.lastFetchedAt, ACCOUNT_SETTINGS_TTL_MS)) {
      return current.data;
    }

    if (accountSettingsInFlightRef.current) {
      return accountSettingsInFlightRef.current;
    }

    setAccountSettings((prev) => ({ ...prev, isFetching: true, error: null }));

    const request = getAccountSettings()
      .then((response) => {
        const next = response?.accountSettings || response?.settings || response?.data?.accountSettings || EMPTY_ACCOUNT_SETTINGS;
        setAccountSettings({
          data: next,
          lastFetchedAt: Date.now(),
          isFetching: false,
          error: null,
        });
        return next;
      })
      .catch((error) => {
        setAccountSettings((prev) => ({ ...prev, isFetching: false, error }));
        return accountSettingsRef.current.data;
      })
      .finally(() => {
        accountSettingsInFlightRef.current = null;
      });

    accountSettingsInFlightRef.current = request;
    return request;
  }, [token, user?.id, isFresh]);

  const getProfileCached = useCallback(async ({ force = false } = {}) => {
    if (!token || !user?.id) return profileRef.current.data;

    const current = profileRef.current;
    if (!force && isFresh(current.lastFetchedAt, PROFILE_TTL_MS)) {
      return current.data;
    }

    if (profileInFlightRef.current) {
      return profileInFlightRef.current;
    }

    setProfile((prev) => ({ ...prev, isFetching: true, error: null }));

    const request = getUserProfile()
      .then((response) => {
        const next = response?.profile || response?.user || response || EMPTY_PROFILE;
        setProfile({
          data: next,
          lastFetchedAt: Date.now(),
          isFetching: false,
          error: null,
        });
        return next;
      })
      .catch((error) => {
        setProfile((prev) => ({ ...prev, isFetching: false, error }));
        return profileRef.current.data;
      })
      .finally(() => {
        profileInFlightRef.current = null;
      });

    profileInFlightRef.current = request;
    return request;
  }, [token, user?.id, isFresh]);

  const getGroupsCached = useCallback(async ({ force = false } = {}) => {
    const companyId = user?.companyId;
    const userId = user?.id;
    if (!token || !companyId || !userId) return groupsRef.current.data;

    const current = groupsRef.current;
    if (!force && isFresh(current.lastFetchedAt, GROUPS_TTL_MS)) {
      return current.data;
    }

    if (groupsInFlightRef.current) {
      return groupsInFlightRef.current;
    }

    setGroups((prev) => ({ ...prev, isFetching: true, error: null }));

    const request = listGroups(companyId, null, userId)
      .then((response) => {
        const next = response?.groups || EMPTY_GROUPS;
        setGroups({
          data: next,
          lastFetchedAt: Date.now(),
          isFetching: false,
          error: null,
        });
        return next;
      })
      .catch((error) => {
        setGroups((prev) => ({ ...prev, isFetching: false, error }));
        return groupsRef.current.data;
      })
      .finally(() => {
        groupsInFlightRef.current = null;
      });

    groupsInFlightRef.current = request;
    return request;
  }, [token, user?.companyId, user?.id, isFresh]);

  const invalidateAccountSettings = useCallback(() => {
    setAccountSettings((prev) => ({ ...prev, lastFetchedAt: 0 }));
  }, []);

  const invalidateProfile = useCallback(() => {
    setProfile((prev) => ({ ...prev, lastFetchedAt: 0 }));
  }, []);

  const invalidateGroups = useCallback(() => {
    setGroups((prev) => ({ ...prev, lastFetchedAt: 0 }));
  }, []);

  const value = useMemo(() => ({
    accountSettings,
    profile,
    groups,
    getAccountSettings: getAccountSettingsCached,
    getProfile: getProfileCached,
    getGroups: getGroupsCached,
    invalidateAccountSettings,
    invalidateProfile,
    invalidateGroups,
    resetSessionData,
  }), [
    accountSettings,
    profile,
    groups,
    getAccountSettingsCached,
    getProfileCached,
    getGroupsCached,
    invalidateAccountSettings,
    invalidateProfile,
    invalidateGroups,
    resetSessionData,
  ]);

  return (
    <SessionDataContext.Provider value={value}>
      {children}
    </SessionDataContext.Provider>
  );
};

export const useSessionData = () => {
  const context = useContext(SessionDataContext);
  if (!context) {
    throw new Error('useSessionData must be used within a SessionDataProvider');
  }
  return context;
};

export default SessionDataContext;
